import * as React from "react";
import { cn } from "@/lib/utils";

export interface BusyIndicatorProps {
  /** Size of the spinner in pixels */
  size?: "sm" | "md" | "lg" | "xl";
  /** Optional message to display below the spinner */
  message?: string;
  /** Array of messages to cycle through */
  messages?: string[];
  /** Interval in milliseconds for cycling messages */
  messageInterval?: number;
  /** Text color for the message */
  textColor?: string;
  /** Additional CSS classes */
  className?: string;
  /** Show background container */
  showContainer?: boolean;
  /** Container background color/gradient */
  containerBg?: string;
  /** Show animated background effects */
  showBackgroundEffects?: boolean;
}

const sizeMap = {
  sm: {
    spinner: "h-4 w-4",
    border: "1.5px",
    innerSize: "calc(50% - 0.4px)",
    text: "text-[10px]",
    gap: "gap-2",
  },
  md: {
    spinner: "h-5 w-5",
    border: "1.5px",
    innerSize: "calc(50% - 0.5px)",
    text: "text-[11px]",
    gap: "gap-3",
  },
  lg: {
    spinner: "h-8 w-8",
    border: "2px",
    innerSize: "calc(50% - 0.8px)",
    text: "text-sm",
    gap: "gap-4",
  },
  xl: {
    spinner: "h-12 w-12",
    border: "2.5px",
    innerSize: "calc(50% - 1.25px)",
    text: "text-base",
    gap: "gap-5",
  },
};

export const BusyIndicator = React.forwardRef<
  HTMLDivElement,
  BusyIndicatorProps
>(
  (
    {
      size = "md",
      message,
      messages,
      messageInterval = 1500,
      textColor = "text-[#EDC29C]",
      className,
      showContainer = false,
      containerBg = "bg-[#161616]",
      showBackgroundEffects = false,
    },
    ref
  ) => {
    const [messageIndex, setMessageIndex] = React.useState(0);

    React.useEffect(() => {
      if (!messages || messages.length <= 1) return;

      const interval = setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length);
      }, messageInterval);

      return () => clearInterval(interval);
    }, [messages, messageInterval]);

    const displayMessage = React.useMemo(() => {
      if (message) return message;
      if (messages && messages.length > 0) return messages[messageIndex];
      return undefined;
    }, [message, messages, messageIndex]);

    const sizes = sizeMap[size];

    const spinnerContent = (
      <div
        className={cn(
          "relative flex flex-col items-center",
          sizes.gap,
          className
        )}
      >
        <div
          className={cn("rounded-full animate-spin", sizes.spinner)}
          style={{
            animationDuration: "0.8s",
            animationTimingFunction: "linear",
            background: `conic-gradient(from 0deg, #161616 0deg, #EDC29C 360deg)`,
            WebkitMask: `radial-gradient(circle, transparent ${sizes.innerSize}, white ${sizes.innerSize})`,
            mask: `radial-gradient(circle, transparent ${sizes.innerSize}, white ${sizes.innerSize})`,
          }}
        />
        {displayMessage && (
          <p
            className={cn(
              "max-w-[140px] text-center tracking-wide",
              sizes.text,
              textColor
            )}
            style={{
              fontFamily: "var(--font-geist-mono), monospace",
              fontWeight: 500,
            }}
          >
            {displayMessage}
          </p>
        )}
      </div>
    );

    if (!showContainer) {
      return (
        <div ref={ref} className="inline-flex">
          {spinnerContent}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          "group relative flex items-center justify-center overflow-hidden rounded-xl",
          containerBg
        )}
      >
        {showBackgroundEffects && (
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] opacity-60" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(237,194,156,0.03),transparent_60%)]" />
          </>
        )}
        {spinnerContent}
      </div>
    );
  }
);

BusyIndicator.displayName = "BusyIndicator";
